/**
 * Auth Change Invalidation Plugin
 * 
 * Automatically invalidates React Query cache when auth state changes.
 * Companion plugin to urlChangeInvalidator.
 * 
 * ## Problem This Solves
 * 
 * Some API endpoints return different data based on authentication:
 * - Availability endpoints show "Subscription required" for unauthenticated users
 * - Validation endpoints check user's subscription limits
 * - User-specific pricing and discounts
 * 
 * If these queries are cached without user context in the key, users see stale
 * data after logging in/out.
 * 
 * ## How It Works
 * 
 * 1. Subscribes to auth events (login, logout, sessionRestore)
 * 2. When auth changes, invalidates auth-sensitive queries
 * 3. Components automatically refetch with new auth context
 * 
 * ## What Gets Invalidated
 * 
 * ### On Login/Session Restore
 * - Availability queries (may show membership access now)
 * - Validation queries (subscription limits change)
 * - Subscription queries (user may have active membership)
 * 
 * ### On Logout
 * - Same queries invalidated
 * - User sees public/unauthenticated data
 * 
 * ## Usage
 * 
 * In main.tsx - one-time setup alongside URL change plugin:
 * 
 * ```typescript
 * import { registerAuthChangeInvalidation } from '~/lib/query/plugins';
 * 
 * const queryClient = new QueryClient({...});
 * registerAuthChangeInvalidation(queryClient);
 * ```
 * 
 * @module query/plugins
 */

import type { QueryClient } from '@tanstack/react-query';
import { authEvents } from '~/services/auth/events';

/**
 * Query key patterns that are sensitive to auth state changes.
 * 
 * These queries return different data based on whether user is authenticated
 * and their subscription status.
 */
const AUTH_SENSITIVE_PATTERNS = [
  'availability',   // Bay availability - shows subscription access
  'validation',     // Booking validation - checks subscription limits
  'subscriptions',  // Active subscriptions
] as const;

/**
 * Check if a query key matches auth-sensitive patterns
 * 
 * Uses exact string matching on individual segments to avoid false positives.
 * For example:
 * - ['clubId', 123, 'bays', 'availability'] → matches (segment 'availability')
 * - ['clubId', 123, 'bays', 'bayAvailability'] → does NOT match (different segment)
 * - ['clubId', 123, 'subscriptions', 'list'] → matches (segment 'subscriptions')
 */
function isAuthSensitiveQuery(queryKey: readonly unknown[]): boolean {
  return queryKey.some(segment => {
    // Only check string segments
    if (typeof segment !== 'string') return false;
    
    // Check if this segment exactly matches any auth-sensitive pattern
    return AUTH_SENSITIVE_PATTERNS.some(pattern => segment === pattern);
  });
}

/**
 * Register automatic cache invalidation on auth state changes
 * 
 * Call this ONCE during app initialization (in main.tsx).
 * It will automatically invalidate auth-sensitive queries when user logs in/out.
 * 
 * @param queryClient - React Query client instance
 * @returns Cleanup function to unsubscribe from auth events
 * 
 * @example
 * ```typescript
 * // In main.tsx
 * const queryClient = new QueryClient({...});
 * registerAuthChangeInvalidation(queryClient);
 * ```
 */
export function registerAuthChangeInvalidation(queryClient: QueryClient): () => void {
  /**
   * Invalidate auth-sensitive queries
   * 
   * Called on login, logout, and session restore.
   */
  const invalidateAuthSensitiveQueries = (event: string, userId?: string | number | null) => {
    console.log(`[AuthInvalidator] Auth event: ${event}`, {
      userId: userId ?? 'null',
      action: 'Invalidating auth-sensitive queries'
    });

    // Invalidate queries that depend on auth state
    queryClient.invalidateQueries({
      predicate: (query) => isAuthSensitiveQuery(query.queryKey)
    });

    console.log('[AuthInvalidator] Auth-sensitive queries invalidated');
  };

  // Subscribe to auth events
  const unsubscribeLogin = authEvents.on('login', (userId) => {
    invalidateAuthSensitiveQueries('login', userId);
  });

  const unsubscribeLogout = authEvents.on('logout', () => {
    invalidateAuthSensitiveQueries('logout', null);
  });

  const unsubscribeSessionRestore = authEvents.on('sessionRestore', (userId) => {
    invalidateAuthSensitiveQueries('sessionRestore', userId);
  });

  // Return cleanup function
  return () => {
    unsubscribeLogin();
    unsubscribeLogout();
    unsubscribeSessionRestore();
  };
}
